import React, { use, useEffect, useRef, useState } from 'react';
import { useBoolean, UseBoolean } from 'react-hanger';
import { useMutation, useQuery } from '@apollo/client/react';
import { getter, orderBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    GridCustomCellProps,
    GridDataType,
    GridSelectionChangeEvent,
    GridFilterChangeEvent,
} from '@progress/kendo-react-grid';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import ToggleDeletedRecordsIcon from '@components-clean/core/grid/deleted-records-icon';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { ActionsGridParent, RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import {
    ExtendedCompleteRiskFields,
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import AddActionDialog, { RiskActionFormInputs } from '@pages-clean/risk-assessment/risk-log/dialogs/add-action-dialog';
import DeleteActionDialog from '@pages-clean/risk-assessment/risk-log/dialogs/delete-action-dialog';
import EmailActionsDialog from '@pages-clean/risk-assessment/risk-log/dialogs/email-actions/email-actions-dialog';
import RestoreActionDialog from '@pages-clean/risk-assessment/risk-log/dialogs/restore-action-dialog';
import { mapActionToFlattenedAction } from '@pages-clean/risk-assessment/risk-log/helper';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import { assumeUtc } from '@utils-clean/date-time-helpers';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    NamedItemType,
    RiskActionType,
} from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import { CompleteRiskFields } from '@queries/generated/fragments.generated';
import {
    riskObservations_OpraQueries_riskObservations_RiskObservation,
    riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
} from '@queries/generated/legacy-risk-assessment.generated';
import { saveRiskAction, saveRiskActionVariables } from '@queries/generated/risk-assessment.generated';
import {
    GET_RISK_OBSERVATIONS_AND_ACTIONS,
    GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
    SAVE_RISK_ACTION,
} from '@queries/risk-assessment';

const CustomCell = (props: GridCustomCellProps) => {
    const { dataItem, tdProps, children } = props;

    if (dataItem.deleted) {
        return <td {...tdProps}>&nbsp;</td>;
    }
    return <td {...tdProps}>{children}</td>;
};

const SharedActionsGrid = ({
    observations,
    selectedAction,
    showDeleted,
    studyId,
    studyNumber,
    risks,
    parent,
    tab,
    setObservationIds,
}: {
    observations: ExtendedRiskObservationFields[];
    selectedAction: ExtendedRiskActionFields | undefined;
    showDeleted: UseBoolean;
    studyId: string;
    studyNumber?: string | null;
    risks: CompleteRiskFields[];
    parent: ActionsGridParent;
    tab?: RiskMonitoringTab;
    setObservationIds?: React.Dispatch<
        React.SetStateAction<{
            observationIds: string[];
            selectedActionCount: number;
        }>
    >;
}) => {
    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const context = parent === ActionsGridParent.RiskLog ? use(RiskLogContext) : use(RiskMonitoringContext);
    const { current, setCurrent } = context;
    const { actionsGridFiltersResetToken } = current as { actionsGridFiltersResetToken?: number };
    const { t, ready } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const exportFileName = t('grids.actions.exportFileName');
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const [flattenedActions, setFlattenedActions] = useState<ExtendedRiskActionFields[] | undefined>(undefined);
    const [currentAction, setCurrentAction] = useState<ExtendedRiskActionFields>();
    const selectedObservationRef = useRef<ExtendedRiskObservationFields>(undefined);
    const showEditActionDialog = useBoolean(false);
    const showDeleteActionDialog = useBoolean(false);
    const showRestoreActionDialog = useBoolean(false);
    const deletedActionIdRef = useRef<string | null>(null);
    const restoredActionIdRef = useRef<string | null>(null);
    const riskActionKeys = [RiskActionType.CORRECTIVE, RiskActionType.PREVENTATIVE];
    const SELECTED_FIELD = 'selected';
    const DATA_ITEM_KEY = 'id';
    const SELECTION_DISABLED_KEY = 'deleted';
    const idGetter = getter(DATA_ITEM_KEY);
    const [selectedState, setSelectedState] = useState<SelectDescriptor>({});
    const clearSelectedRowsEvent = useBoolean(false);
    const showEmailActions = useBoolean(false);
    const showEmailWarningDialog = useBoolean(false);
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const lastClearFiltersTokenRef = useRef<number | undefined>(undefined);

    const [saveActionMutation] = useMutation<saveRiskAction, saveRiskActionVariables>(SAVE_RISK_ACTION);
    const showColumnsByDefault = (tab && tab === RiskMonitoringTab.ManageActions) ?? false;

    const { data: responsibleFunctionsData } = useQuery<getNamedItemsByTypeForStudy>(
        GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY,
        {
            variables: {
                studyId,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        },
    );

    const gridKey =
        parent === ActionsGridParent.RiskLog
            ? PersistentStorageKeys['risk-log-monitoring-actions']
            : parent === ActionsGridParent.RiskMonitoring && tab === undefined
              ? PersistentStorageKeys['risk-monitoring-child-actions']
              : PersistentStorageKeys['risk-monitoring-actions'];

    useEffect(() => {
        if (responsibleFunctionsData && observations && risks && ready) {
            const actionsList: ExtendedRiskActionFields[] = [];
            const riskIds = risks.filter((risk) => risk !== undefined).map((r: { id: string }) => r.id);
            (showDeleted.value
                ? observations.filter((ob) => ob !== undefined)
                : observations.filter((ob) => ob !== undefined).filter((ob) => !ob.deleted)
            )
                .filter((ob) => riskIds.includes(ob.riskId))
                .forEach((item) => {
                    const riskRecordId = risks.find((risk: { id: string }) => risk.id === item.riskId)!.recordId;
                    if (item.actions) {
                        const curActions = (
                            showDeleted.value
                                ? item.actions
                                : item.actions.filter((actions) => actions && !actions.deleted)
                        )
                            .filter((actions) => actions != null)
                            .map((action) => ({
                                ...mapActionToFlattenedAction(
                                    action as unknown as riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
                                    item as unknown as riskObservations_OpraQueries_riskObservations_RiskObservation,
                                    t(`shared.${action?.type?.toLowerCase()}`) as string,
                                    riskRecordId,
                                ),
                                [SELECTED_FIELD]: selectedState[idGetter(action)],
                            }));
                        if (curActions) actionsList.push(...(curActions.filter(Boolean) as ExtendedRiskActionFields[]));
                    }
                });

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFlattenedActions(
                orderBy(actionsList.filter(Boolean) as ExtendedRiskActionFields[], [
                    { field: 'status', dir: 'desc' },
                    { field: 'updated', dir: 'desc' },
                ]),
            );

            if (restoredActionIdRef.current) {
                const updateAction = actionsList.find((action) => action.id === restoredActionIdRef.current);
                setCurrentAction(updateAction);
                return;
            }

            if (!actionsList.some((action) => action.id === currentAction?.id)) {
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/set-state-in-effect
                setCurrentAction(undefined);
            }
        } else {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setFlattenedActions([]);
            setCurrentAction(undefined);
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [observations, risks, responsibleFunctionsData, showDeleted.value, ready]);

    // It's vital this useEffect is after the above (which can set current action to undefined if it's not in the actions list). Both effects
    // are triggered when a new action is added so if this isn't run after the other the new action won't be selected.
    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setCurrentAction(selectedAction);
    }, [selectedAction]);

    const onEditActionClose = () => {
        showEditActionDialog.setFalse();
    };

    const onSubmitEditAction = async (editActionData: RiskActionFormInputs) => {
        await saveActionMutation({
            variables: {
                action: {
                    id: currentAction?.id,
                    type: RiskActionType[editActionData.type?.id as keyof typeof RiskActionType],
                    ownerId: editActionData.owner?.id,
                    responsibleFunctionId: editActionData.responsibleFunction?.id,
                    requestDate: assumeUtc(editActionData.requestedDate),
                    description: editActionData.description,
                    result: editActionData.result,
                    comments: editActionData.comments,
                    expectedCompleteDate: editActionData.expectedCompletionDate
                        ? assumeUtc(editActionData.expectedCompletionDate)
                        : undefined,
                    completedDate: editActionData.completedDate ? assumeUtc(editActionData.completedDate) : undefined,
                },
                riskId: selectedObservationRef.current?.riskId,
                observationId: selectedObservationRef.current?.id,
            },
            refetchQueries:
                parent === ActionsGridParent.RiskLog
                    ? [
                          {
                              query: GET_RISK_OBSERVATIONS_AND_ACTIONS,
                              variables: {
                                  studyId,
                                  riskId: selectedObservationRef.current?.riskId,
                              },
                          },
                      ]
                    : [
                          {
                              query: GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
                              variables: {
                                  studyId,
                              },
                          },
                      ],
        })
            .then((response) => {
                if (!response?.data || !response.data?.saveRiskAction) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showEditActionDialog.setFalse();
                const riskRecordId = risks.find(
                    (risk: { id: string }) => risk.id === selectedObservationRef.current?.riskId,
                )!.recordId;
                setCurrentAction(
                    mapActionToFlattenedAction(
                        response.data
                            ?.saveRiskAction as riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
                        selectedObservationRef as unknown as riskObservations_OpraQueries_riskObservations_RiskObservation,
                        t(`shared.${response.data?.saveRiskAction?.type?.toLowerCase()}`) as string,
                        riskRecordId,
                    ) ?? undefined,
                );
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onDeleteActionClose = () => {
        showDeleteActionDialog.setFalse();
    };

    const onSubmitDeleteAction = () => {
        deletedActionIdRef.current = currentAction?.id;
        clearSelectedRowsEvent.setTrue();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showDeleteActionDialog.setFalse();
    };

    const onRestoreActionClose = () => {
        showRestoreActionDialog.setFalse();
    };

    const onSubmitRestoreAction = () => {
        restoredActionIdRef.current = currentAction?.id;
        if (!showDeleted.value) clearSelectedRowsEvent.setTrue();
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showRestoreActionDialog.setFalse();
    };

    const setSelectedObservationIds = (newSelectedState: { [x: string]: boolean | number[] }) => {
        if (setObservationIds) {
            const actionIdList = flattenedActions
                ?.filter((x) => newSelectedState[idGetter(x)] === true)
                .map((action) => action.id);

            const observationIds = flattenedActions
                ?.filter((ac: { id: string }) => !!actionIdList?.includes(ac.id))
                .map((actions) => actions.observationId);

            setObservationIds({ observationIds: observationIds ?? [], selectedActionCount: actionIdList?.length ?? 0 });
        }
    };

    const columns: GridColumnProps[] = [
        ...(showDeleted.value
            ? [
                  {
                      id: 'deleted',
                      field: 'deleted',
                      title: t('grids.actions.columns.isDeleted'),
                      lockedByDefault: true,
                      cells: { data: YesNoCell },
                      width: 1,
                      minWidth: 100,
                      filter: 'boolean' as GridDataType,
                  },
              ]
            : []),
        {
            id: 'status',
            field: 'status',
            title: t('grids.actions.columns.status'),
            lockedByDefault: true,
            width: 1,
            minWidth: 120,
            lovData: [t('shared.open'), t('shared.completed')],
        },
        {
            id: 'riskId',
            field: 'riskRecordId',
            title: t('grids.actions.columns.riskId'),
            lockedByDefault: true,
            minWidth: 100,
            width: 1,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'statement',
            field: 'riskStatement',
            title: t('grids.actions.columns.statement'),
            lockedByDefault: true,
            minWidth: 250,
            width: 1,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            showByDefault: showColumnsByDefault,
            alwaysExportColumn: true,
        },
        {
            id: 'observation',
            field: 'observation',
            title: t('grids.actions.columns.observation'),
            lockedByDefault: true,
            minWidth: 250,
            width: 1,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            showByDefault: showColumnsByDefault,
            alwaysExportColumn: true,
        },
        {
            id: 'description',
            field: 'description',
            title: t('grids.actions.columns.description'),
            lockedByDefault: true,
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'type',
            field: 'type',
            title: t('grids.actions.columns.type'),
            width: 1,
            minWidth: 180,
            lovData: riskActionKeys.map((key) => t(`shared.${key.toLowerCase()}`)),
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'requestDate',
            field: 'requestDate',
            title: t('grids.actions.columns.requestedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            id: 'comments',
            field: 'comments',
            title: t('grids.actions.columns.comments'),
            minWidth: 250,
            width: 1,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'result',
            field: 'result',
            title: t('grids.actions.columns.result'),
            minWidth: 250,
            width: 1,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'owner',
            field: 'owner',
            title: t('grids.actions.columns.owner'),
            width: 1,
            minWidth: 150,
            filterKey: 'name',
            filter: 'text',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'responsibleFunction',
            field: 'responsibleFunction',
            title: t('grids.actions.columns.responsibleFunction'),
            minWidth: 180,
            width: 1,
            filter: 'text',
            filterKey: 'name',
            lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                ? responsibleFunctionsData.namedItemsByTypeForStudy
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'expectedCompleteDate',
            field: 'expectedCompleteDate',
            title: t('grids.actions.columns.expectedCompletionDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 170,
            cellName: 'DateCell',
        },
        {
            id: 'completedDate',
            field: 'completedDate',
            title: t('grids.actions.columns.completedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'updatedBy',
            field: 'updatedBy',
            title: t('grids.actions.columns.updatedBy'),
            width: 1,
            minWidth: 150,
        },
        {
            id: 'updated',
            field: 'updated',
            title: t('grids.actions.columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
        },
        ...(showDeleted.value
            ? [
                  {
                      id: 'reasonForDeletion',
                      field: 'reasonForDeletion',
                      title: t('grids.actions.columns.reasonForDeletion'),
                      minWidth: 250,
                      width: 1,
                      filter: 'text' as GridDataType,
                      cells: {
                          data: TooltipCell({
                              isCsv: false,
                          }),
                      },
                  },
                  {
                      id: 'deletedBy',
                      field: 'deletedBy',
                      title: t('grids.actions.columns.deletedBy'),
                      width: 1,
                      minWidth: 150,
                  },
                  {
                      id: 'deletedDate',
                      field: 'deletedDate',
                      title: t('grids.actions.columns.deleted'),
                      filter: 'date' as GridDataType,
                      cells: { data: DateTimeCell('DD-MMM-yyyy') },
                      width: 1,
                      minWidth: 130,
                      cellName: 'DateCell',
                  },
              ]
            : []),
        {
            id: 'createdBy',
            field: 'createdBy',
            title: t('grids.actions.columns.createdBy'),
            width: 1,
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: t('grids.actions.columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        {
            width: 1,
            minWidth: 120,
            id: 'recordId',
            field: 'recordId',
            showByDefault: false,
            title: 'ID',
        },
    ];

    const customGridMenuItems: GridMenuItemProps[] = [
        {
            'data-testid': 'grid-show-deleted-records',
            customIcon: <ToggleDeletedRecordsIcon showDeleted={showDeleted.value} />,
            onClick: () => showDeleted.toggle(),
            ignoreReadOnlyContext: true,
            title: showDeleted.value ? tCommon('hideDeletedRecords') : tCommon('displayDeletedRecords'),
        },
    ];

    const clearActionsFilter = React.useCallback((): Promise<boolean | void> => {
        clearSelectedRowsEvent.setTrue();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setFilterState({
            logic: 'and',
            filters: [],
        });
        setCurrent((prev) => ({
            ...prev,
            actionsGridIsFiltered: false,
        }));
        setSelectedState({});
        /* eslint-enable @eslint-react/set-state-in-effect */
        return Promise.resolve();
    }, [clearSelectedRowsEvent, setCurrent]);

    useEffect(() => {
        if (actionsGridFiltersResetToken == null) return;
        if (lastClearFiltersTokenRef.current === actionsGridFiltersResetToken) return;

        lastClearFiltersTokenRef.current = actionsGridFiltersResetToken;

        clearActionsFilter().catch(() => undefined);
    }, [actionsGridFiltersResetToken, clearActionsFilter]);

    const onSelectionChange = (_event: GridSelectionChangeEvent, selected: { [x: string]: boolean | number[] }) => {
        restoredActionIdRef.current = null;
        clearSelectedRowsEvent.setFalse();
        setSelectedState(selected);
        setSelectedObservationIds(selected);
    };

    const handleFilterChange = (event: GridFilterChangeEvent): Promise<boolean | void> => {
        clearSelectedRowsEvent.setTrue();
        setSelectedState({});
        const nextFilter = event.filter as CompositeFilterDescriptor | undefined;
        setFilterState(nextFilter);
        const hasFiltersApplied = nextFilter ? nextFilter.filters.length > 0 : false;
        setCurrent((prev) => ({
            ...prev,
            actionsGridIsFiltered: hasFiltersApplied,
        }));
        return Promise.resolve();
    };

    const emailCallback = () => {
        const actions = flattenedActions?.filter((x) => selectedState[idGetter(x)] === true);
        if (actions) {
            let isValid = true;
            let actionOwner: string | null | undefined;
            actions.forEach((action) => {
                if (actionOwner === undefined) {
                    actionOwner = action.owner.email;
                }
                if (
                    action.deleted ||
                    action.owner == null ||
                    action.owner.email == null ||
                    !action.owner.active ||
                    action.owner.email !== actionOwner
                ) {
                    isValid = false;
                }
            });
            if (isValid) showEmailActions.setTrue();
            else showEmailWarningDialog.setTrue();
        }
    };

    if (
        parent === ActionsGridParent.RiskLog &&
        (showDeleted.value
            ? observations.filter((ob) => ob !== undefined)
            : observations.filter((ob) => ob !== undefined).filter((ob) => !ob.deleted)
        ).length === 0
    )
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('grids.actions.info.noParentRecordSelected')} layout="center" />
            </div>
        );

    const getEmailData = () => {
        const actionList = flattenedActions?.filter((x) => selectedState[idGetter(x)] === true) ?? [];

        const observationList = observations.filter(
            (ob: { id: string }) => !!actionList.find((ac) => ob.id === ac.observationId),
        );
        const riskList = risks.filter((ri: { id: string }) => !!observationList.find((ob) => ri.id === ob.riskId));

        return {
            risks: riskList as unknown[] as ExtendedCompleteRiskFields[],
            observations: observationList,
            actions: actionList,
        };
    };

    return (
        <>
            <Grid
                excludeHiddenRowMenuActions
                title={`${t('grids.actions.title')} ${showDeleted.value ? ` - ${tCommon('all')}` : ''}`}
                customGridMenuItems={customGridMenuItems}
                maxHeight="100%"
                testDataId="risklog-actions-grid"
                showSendEmailIcon
                emailCallback={emailCallback}
                selectedField={SELECTED_FIELD}
                selectionDisabledField={SELECTION_DISABLED_KEY}
                selectable={{ enabled: true, mode: 'multiple', drag: true }}
                cells={{
                    select: {
                        data: CustomCell,
                    },
                }}
                onSelectionChange={onSelectionChange}
                defaultSelectedItem={currentAction}
                clearGridSelectedRows={clearSelectedRowsEvent.value}
                filter={filterState}
                onFilterChange={handleFilterChange}
                clearFilter={clearActionsFilter}
                data={flattenedActions}
                columns={columns}
                filterable
                showAddIcon={false}
                useLoadingOverlay
                canEdit={canEdit}
                scrollable="virtual"
                dataItemKey={DATA_ITEM_KEY}
                exportFilename={`${exportFileName}_${studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                rowMenuActionsWidth={50}
                persistentStorageKey={String(gridKey)}
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.actions.actions.edit'),
                        onClick: (dataItem) => {
                            const action = dataItem as ExtendedRiskActionFields;
                            selectedObservationRef.current = observations?.find((ob) => ob.id === action.observationId);
                            setCurrentAction(action);
                            showEditActionDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const action = dataItem as ExtendedRiskActionFields;
                            return !action.deleted;
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('grids.actions.actions.delete'),
                        onClick: (dataItem) => {
                            const action = dataItem as ExtendedRiskActionFields;
                            selectedObservationRef.current = observations?.find((ob) => ob.id === action.observationId);
                            setCurrentAction(action);
                            showDeleteActionDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskActionFields;
                            return !obs.deleted;
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                    {
                        id: 3,
                        label: t('grids.actions.actions.restore'),
                        onClick: (dataItem) => {
                            const action = dataItem as ExtendedRiskActionFields;
                            selectedObservationRef.current = observations?.find((ob) => ob.id === action.observationId);
                            setCurrentAction(action);
                            showRestoreActionDialog.setTrue();
                        },
                        showMenuItem: (dataItem) => {
                            const obs = dataItem as ExtendedRiskActionFields;
                            return obs.deleted ?? false;
                        },
                        icon: ['far', 'arrow-rotate-backward'],
                        disabled: !canEdit,
                    },
                ]}
            />
            {showEditActionDialog.value && selectedObservationRef.current && (
                <AddActionDialog
                    onClose={onEditActionClose}
                    onSubmit={onSubmitEditAction}
                    studyRef={studyId}
                    observation={selectedObservationRef.current}
                    action={currentAction}
                />
            )}
            {showDeleteActionDialog.value && selectedObservationRef.current && currentAction && (
                <DeleteActionDialog
                    onClose={onDeleteActionClose}
                    onSubmit={onSubmitDeleteAction}
                    observation={selectedObservationRef.current}
                    action={currentAction}
                    studyId={studyId}
                    riskId={selectedObservationRef.current?.riskId}
                />
            )}
            {showRestoreActionDialog.value && selectedObservationRef.current && currentAction && (
                <RestoreActionDialog
                    onClose={onRestoreActionClose}
                    onSubmit={onSubmitRestoreAction}
                    observation={selectedObservationRef.current}
                    action={currentAction}
                    studyId={studyId}
                    riskId={selectedObservationRef.current?.riskId}
                    parent={parent}
                />
            )}
            {showEmailActions.value && (
                <EmailActionsDialog
                    studyRef={studyId}
                    {...getEmailData()}
                    onClose={(message: string) => {
                        if (message === 'success') ShowSuccess(tCommon('actionsSuccessfulToast'));
                        showEmailActions.setFalse();
                    }}
                    canEdit={canEdit}
                />
            )}
            {showEmailWarningDialog.value && (
                <ConfirmationDialog
                    title={t('dialogs.emailActions.warningTitle')}
                    dataTestId="email-actions-error-dialog"
                    onClose={() => showEmailWarningDialog.setFalse()}
                    secondaryBtnLabel={tCommon('close')}
                    width={500}
                >
                    <InfoField type="Warning" message={t('dialogs.emailActions.warningBody')} />
                </ConfirmationDialog>
            )}
        </>
    );
};

export default SharedActionsGrid;
